import { Grid } from '@radix-ui/themes';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonSearchInput } from 'components/common/form/search';
import { CommonSelectInput } from 'components/common/form/select';
import useHitterProfileStore from 'components/sections/hitter-library/profile/use-hitter-profile-store';
import { format } from 'date-fns-tz';
import { HPSummarySection } from 'enums/hitter-profile.enums';
import { t } from 'i18next';
import { HitterProfileHelper } from 'lib_ts/classes/hitter-profile.helper';
import {
  LOCAL_DATEPICKER_FORMAT,
  LOCAL_TIMEZONE,
} from 'lib_ts/enums/datetime.enums';
import { HitterProfilePeriod } from 'lib_ts/enums/hitters.enums';
import {
  PITCH_TYPE_OPTIONS,
  PITCHER_HANDS,
  PitcherHand,
  PitchType,
} from 'lib_ts/enums/pitches.enums';
import { IOption } from 'lib_ts/interfaces/common/i-option';
import { useMemo } from 'react';

const SHOW_PITCHER_HAND_FILTER = false;

export const HitterProfileSummaryFilters = () => {
  const hitsFilter = useHitterProfileStore((s) => s.hitsFilter);
  const summarySections = useHitterProfileStore((s) => s.summarySections);
  const summaryFilter = useHitterProfileStore((s) => s.summaryFilter);
  const loading = useHitterProfileStore((s) => s.loading);

  const PERIOD_OPTIONS = useMemo(() => {
    const end = format(new Date(), LOCAL_DATEPICKER_FORMAT, {
      timeZone: LOCAL_TIMEZONE,
    });

    return Object.values(HitterProfilePeriod).map((v) => {
      const start = format(
        HitterProfileHelper.getCurrentStartDate(v),
        LOCAL_DATEPICKER_FORMAT,
        { timeZone: LOCAL_TIMEZONE }
      );

      const o: IOption = {
        label: `${t(v)} (${start} - ${end})`,
        value: v,
      };

      return o;
    });
  }, []);

  const columns = SHOW_PITCHER_HAND_FILTER
    ? {
        xs: '2',
        md: '4',
      }
    : {
        xs: '2',
        md: '3',
      };

  return (
    <ErrorBoundary identifier="HitterProfileSummaryFilters">
      <Grid columns={columns} gap="2">
        <CommonSelectInput
          id="filter-period"
          placeholder="common.period"
          value={hitsFilter.period}
          options={PERIOD_OPTIONS}
          onChange={(v) =>
            useHitterProfileStore
              .getState()
              .mergeHitsFilter({ period: v as HitterProfilePeriod })
          }
          disabled={loading}
          skipSort
        />
        <CommonSearchInput
          id="filter-sections"
          placeholder="hitters.sections-displayed"
          values={summarySections}
          options={Object.values(HPSummarySection).map((s) => ({
            label: s,
            value: s,
          }))}
          onChange={(v) =>
            useHitterProfileStore
              .getState()
              .setSummarySections(v as HPSummarySection[])
          }
          disabled={loading}
          optional
          skipSort
          multiple
        />
        {SHOW_PITCHER_HAND_FILTER && (
          <CommonSelectInput
            id="filter-pitcher-hand"
            placeholder="common.pitcher-hand"
            value={summaryFilter.pitcherHand || ''}
            options={PITCHER_HANDS}
            onChange={(v) => {
              if (!v) {
                useHitterProfileStore
                  .getState()
                  .mergeSummaryFilter({ pitcherHand: null });
                return;
              }
              useHitterProfileStore
                .getState()
                .mergeSummaryFilter({ pitcherHand: v as PitcherHand });
            }}
            disabled={loading}
            skipSort
            optional
          />
        )}
        <CommonSearchInput
          id="filter-pitch-types"
          placeholder="common.pitch-types"
          values={summaryFilter.pitchTypes}
          options={PITCH_TYPE_OPTIONS}
          onChange={(v) =>
            useHitterProfileStore
              .getState()
              .mergeSummaryFilter({ pitchTypes: v as PitchType[] })
          }
          disabled={loading}
          optional
          skipSort
          multiple
        />
      </Grid>
    </ErrorBoundary>
  );
};
